// src/pages/Terms.tsx
import React from "react";
import { Link } from "react-router-dom";
import {
  Scale,
  Shield,
  AlertCircle,
  BookOpen,
  CheckCircle,
  Sparkles,
  Key,
  Globe,
  Zap,
} from "lucide-react";
import logo from "../assets/images/logo.png";
import PublicNavbar from "../components/PublicNavbar";

const Terms: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 via-gray-100 to-gray-200 dark:from-gray-900 dark:via-gray-800 dark:to-gray-950">
      {/* Navigation */}
      <PublicNavbar currentPage="/terms" />

      {/* Header */}
      <section className="relative overflow-hidden">
        {/* Background Glow Effects */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>

        <div className="container mx-auto px-4 lg:px-5 py-20 relative">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 text-primary text-sm font-medium mb-6 backdrop-blur-sm">
              <Scale className="h-4 w-4 mr-2" />
              Legal Terms
            </div>
            <h1 className="text-4xl lg:text-5xl xl:text-6xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent mb-8">
              Terms & Conditions
            </h1>
            <p className="text-lg lg:text-xl text-gray-600 dark:text-gray-400 mb-12">
              Last updated:{" "}
              {new Date().toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
                year: "numeric",
              })}
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 lg:py-20">
        <div className="container mx-auto px-4 lg:px-5">
          <div className="max-w-4xl mx-auto">
            <div className="space-y-12">
              {/* Agreement to Terms */}
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent rounded-3xl blur-xl opacity-50"></div>
                <div className="relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-2xl border border-gray-200/30 dark:border-gray-700/30 p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <BookOpen className="h-6 w-6 text-primary" />
                    <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white">
                      1. Agreement to Terms
                    </h2>
                  </div>
                  <div className="space-y-4 text-gray-600 dark:text-gray-400">
                    <p>
                      Welcome to SyvronRecovery. These Terms and Conditions
                      constitute a legally binding agreement made between you,
                      whether personally or on behalf of an entity ("you") and
                      SyvronRecovery ("we", "us", or "our"), concerning your
                      access to and use of our services.
                    </p>
                    <p>
                      By accessing or using our website, mobile application, or
                      any other services (collectively, the "Services"), you
                      acknowledge that you have read, understood, and agree to
                      be bound by these Terms. If you do not agree with all of
                      these Terms, you are prohibited from using the Services
                      and must discontinue use immediately.
                    </p>
                  </div>
                </div>
              </div>

              {/* Services Description */}
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-blue-500/10 via-blue-500/5 to-transparent rounded-3xl blur-xl opacity-50"></div>
                <div className="relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-2xl border border-gray-200/30 dark:border-gray-700/30 p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <Shield className="h-6 w-6 text-blue-500" />
                    <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white">
                      2. Services Description
                    </h2>
                  </div>
                  <div className="space-y-6">
                    <p className="text-gray-600 dark:text-gray-400">
                      SyvronRecovery provides the following advanced protection
                      and recovery services:
                    </p>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-xl p-5 border border-primary/20 group hover:scale-102 transition-all duration-300">
                        <div className="flex items-center gap-3 mb-3">
                          <Key className="h-5 w-5 text-primary" />
                          <h3 className="font-bold text-gray-900 dark:text-white">
                            AI Market Protection
                          </h3>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Intelligent monitoring of connected crypto wallets to
                          detect market downturns and trigger protection
                          protocols.
                        </p>
                      </div>
                      <div className="bg-gradient-to-br from-green-500/5 to-green-500/10 rounded-xl p-5 border border-green-500/20 group hover:scale-102 transition-all duration-300">
                        <div className="flex items-center gap-3 mb-3">
                          <Zap className="h-5 w-5 text-green-500" />
                          <h3 className="font-bold text-gray-900 dark:text-white">
                            Expert Fund Recovery
                          </h3>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Specialized recovery services for lost, stolen, or
                          hacked cryptocurrency funds through investigation and
                          negotiation.
                        </p>
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-yellow-500/5 to-yellow-500/10 rounded-xl p-5 border border-yellow-500/20">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="h-5 w-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white mb-1">
                            Important Security Notice
                          </p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            SyvronRecovery does NOT store your private keys,
                            seed phrases, or provide wallet services. Our
                            Services monitor wallet activity and provide
                            recovery assistance through external means.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* User Responsibilities */}
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-green-500/10 via-green-500/5 to-transparent rounded-3xl blur-xl opacity-50"></div>
                <div className="relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-2xl border border-gray-200/30 dark:border-gray-700/30 p-8">
                  <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white mb-6">
                    3. User Responsibilities
                  </h2>
                  <div className="space-y-6">
                    <p className="text-gray-600 dark:text-gray-400">
                      By using our Services, you agree to comply with the
                      following responsibilities:
                    </p>

                    <div className="bg-gradient-to-br from-green-500/5 to-green-500/10 rounded-xl p-6 border border-green-500/20">
                      <div className="space-y-4">
                        {[
                          "Provide accurate and complete information during registration and verification processes",
                          "Maintain the security of your account credentials and authentication methods",
                          "Use the Services only for lawful purposes and in accordance with these Terms",
                          "Notify us immediately of any unauthorized access or security breaches",
                          "Comply with all applicable laws and regulations in your jurisdiction",
                        ].map((item, idx) => (
                          <div key={idx} className="flex items-start">
                            <CheckCircle className="h-5 w-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700 dark:text-gray-300">
                              {item}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Fees & Payment */}
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-purple-500/10 via-purple-500/5 to-transparent rounded-3xl blur-xl opacity-50"></div>
                <div className="relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-2xl border border-gray-200/30 dark:border-gray-700/30 p-8">
                  <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white mb-6">
                    4. Fees & Payment Terms
                  </h2>
                  <div className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-xl p-5 border border-primary/20">
                        <h4 className="font-bold text-gray-900 dark:text-white mb-3">
                          Market Protection
                        </h4>
                        <div className="space-y-2">
                          <p className="text-sm">
                            Monthly subscription:{" "}
                            <span className="font-semibold">$49.99</span>
                          </p>
                          <p className="text-sm">
                            Annual subscription:{" "}
                            <span className="font-semibold">$499.99</span>
                          </p>
                          <p className="text-xs text-gray-500">
                            Cancel anytime with 30-day notice
                          </p>
                        </div>
                      </div>
                      <div className="bg-gradient-to-br from-green-500/5 to-green-500/10 rounded-xl p-5 border border-green-500/20">
                        <h4 className="font-bold text-gray-900 dark:text-white mb-3">
                          Recovery Services
                        </h4>
                        <div className="space-y-2">
                          <p className="text-sm">
                            Success fee:{" "}
                            <span className="font-semibold">
                              20% of recovered amount
                            </span>
                          </p>
                          <p className="text-sm">
                            Minimum fee:{" "}
                            <span className="font-semibold">$500</span>
                          </p>
                          <p className="text-xs text-gray-500">
                            Fee due only upon successful recovery
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-blue-500/5 to-blue-500/10 rounded-xl p-5 border border-blue-500/20">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white mb-1">
                            Refund & Payment Policy
                          </p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            Subscription fees for Market Protection services are
                            non-refundable. Recovery services operate on a
                            success-fee basis only. All fees are exclusive of
                            applicable taxes.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Disclaimers & Limitations */}
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-red-500/10 via-red-500/5 to-transparent rounded-3xl blur-xl opacity-50"></div>
                <div className="relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-2xl border border-gray-200/30 dark:border-gray-700/30 p-8">
                  <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white mb-6">
                    5. Disclaimers & Limitations of Liability
                  </h2>
                  <div className="space-y-6">
                    <div className="bg-gradient-to-br from-red-500/5 to-red-500/10 rounded-xl p-5 border border-red-500/20">
                      <h4 className="font-bold text-gray-900 dark:text-white mb-3">
                        Service Disclaimers
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                        OUR SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE"
                        WITHOUT WARRANTIES OF ANY KIND. WE DO NOT GUARANTEE:
                      </p>
                      <div className="space-y-2">
                        {[
                          "100% success rate in recovering lost funds",
                          "Complete protection against all market downturns",
                          "Immediate recovery of lost funds",
                          "Protection against all security threats or hacks",
                        ].map((item, idx) => (
                          <div key={idx} className="flex items-start text-sm">
                            <div className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                            <span className="text-gray-700 dark:text-gray-300">
                              {item}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-yellow-500/5 to-yellow-500/10 rounded-xl p-5 border border-yellow-500/20">
                      <h4 className="font-bold text-gray-900 dark:text-white mb-3">
                        Limitation of Liability
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                        TO THE MAXIMUM EXTENT PERMITTED BY LAW, SYVRONRECOVERY
                        SHALL NOT BE LIABLE FOR:
                      </p>
                      <div className="space-y-2">
                        {[
                          "Indirect, incidental, special, consequential, or punitive damages",
                          "Loss of profits, data, use, goodwill, or other intangible losses",
                          "Unauthorized access to or use of your account",
                          "Interruption or cessation of transmission to or from our Services",
                        ].map((item, idx) => (
                          <div key={idx} className="flex items-start text-sm">
                            <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                            <span className="text-gray-700 dark:text-gray-300">
                              {item}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Intellectual Property */}
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent rounded-3xl blur-xl opacity-50"></div>
                <div className="relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-2xl border border-gray-200/30 dark:border-gray-700/30 p-8">
                  <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white mb-6">
                    6. Intellectual Property Rights
                  </h2>
                  <div className="space-y-4 text-gray-600 dark:text-gray-400">
                    <p>
                      The Services and their original content, features, and
                      functionality are and will remain the exclusive property
                      of SyvronRecovery and its licensors. Our trademarks and
                      trade dress may not be used in connection with any product
                      or service without the prior written consent of
                      SyvronRecovery.
                    </p>
                    <p>
                      You are granted a limited, non-exclusive,
                      non-transferable, and revocable license to access and use
                      the Services for their intended purpose.
                    </p>
                  </div>
                </div>
              </div>

              {/* Termination */}
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-blue-500/10 via-blue-500/5 to-transparent rounded-3xl blur-xl opacity-50"></div>
                <div className="relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-2xl border border-gray-200/30 dark:border-gray-700/30 p-8">
                  <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white mb-6">
                    7. Termination
                  </h2>
                  <div className="space-y-4 text-gray-600 dark:text-gray-400">
                    <p>
                      We may terminate or suspend your account and access to our
                      Services immediately, without prior notice or liability,
                      for any reason whatsoever, including without limitation if
                      you breach these Terms.
                    </p>
                    <p>
                      Upon termination, your right to use the Services will
                      immediately cease. All provisions of these Terms which by
                      their nature should survive termination shall survive
                      termination, including, without limitation, ownership
                      provisions, warranty disclaimers, indemnity, and
                      limitations of liability.
                    </p>
                  </div>
                </div>
              </div>

              {/* Governing Law */}
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-green-500/10 via-green-500/5 to-transparent rounded-3xl blur-xl opacity-50"></div>
                <div className="relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-2xl border border-gray-200/30 dark:border-gray-700/30 p-8">
                  <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white mb-6">
                    8. Governing Law
                  </h2>
                  <div className="space-y-4 text-gray-600 dark:text-gray-400">
                    <p>
                      These Terms shall be governed and construed in accordance
                      with the laws of the State of Delaware, United States,
                      without regard to its conflict of law provisions.
                    </p>
                    <p>
                      Our failure to enforce any right or provision of these
                      Terms will not be considered a waiver of those rights. If
                      any provision of these Terms is held to be invalid or
                      unenforceable by a court, the remaining provisions of
                      these Terms will remain in effect.
                    </p>
                  </div>
                </div>
              </div>

              {/* Changes to Terms */}
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-purple-500/10 via-purple-500/5 to-transparent rounded-3xl blur-xl opacity-50"></div>
                <div className="relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-2xl border border-gray-200/30 dark:border-gray-700/30 p-8">
                  <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white mb-6">
                    9. Changes to These Terms
                  </h2>
                  <div className="space-y-4 text-gray-600 dark:text-gray-400">
                    <p>
                      We reserve the right, at our sole discretion, to modify or
                      replace these Terms at any time. If a revision is
                      material, we will provide at least 30 days' notice prior
                      to any new terms taking effect. What constitutes a
                      material change will be determined at our sole discretion.
                    </p>
                    <p>
                      By continuing to access or use our Services after those
                      revisions become effective, you agree to be bound by the
                      revised terms.
                    </p>
                  </div>
                </div>
              </div>

              {/* Contact */}
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-blue-500/10 via-blue-500/5 to-transparent rounded-3xl blur-xl opacity-50"></div>
                <div className="relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-2xl border border-gray-200/30 dark:border-gray-700/30 p-8">
                  <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white mb-6">
                    10. Contact Information
                  </h2>
                  <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-xl p-8 border border-primary/20">
                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                      If you have any questions about these Terms, please
                      contact our Legal Department at:
                    </p>
                    <div className="space-y-2">
                      <p className="font-medium text-gray-900 dark:text-white">
                        Email: legal@syvronrecovery.com
                      </p>
                      <p className="font-medium text-gray-900 dark:text-white">
                        Address: 123 Legal Avenue, Wilmington, Delaware 19801,
                        USA
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-4">
                        For legal notices, please use the mailing address above.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12">
        <div className="container mx-auto px-4 lg:px-5">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <img src={logo} alt="SyvronRecovery" className="h-8 w-auto" />
                <span className="text-xl font-bold text-white">
                  SyvronRecovery
                </span>
              </div>
              <p className="mb-4 text-sm">
                Advanced crypto protection and recovery services combining AI
                technology with expert human oversight.
              </p>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Services</h4>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/wallet"
                    className="hover:text-white transition-colors"
                  >
                    Wallet Protection
                  </Link>
                </li>
                <li>
                  <Link
                    to="/recovery"
                    className="hover:text-white transition-colors"
                  >
                    Fund Recovery
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Legal</h4>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/terms"
                    className="hover:text-white transition-colors"
                  >
                    Terms & Conditions
                  </Link>
                </li>
                <li>
                  <Link
                    to="/privacy"
                    className="hover:text-white transition-colors"
                  >
                    Privacy Policy
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Contact</h4>
              <p className="mb-2 text-sm">support@syvronrecovery.com</p>
              <p className="text-sm">24/7 Support Available</p>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-sm">
            <p>
              © {new Date().getFullYear()} SyvronRecovery. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Terms;
